<script setup>
import { computed, provide, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAccount } from 'dashboard/composables/useAccount';
import ReportsHomeTabs from './ReportsHomeTabs.vue';
import ReportTimezoneNote from './ReportTimezoneNote.vue';
import ReportsPeriodBar from './ReportsPeriodBar.vue';
import { useReportsUsage } from '../composables/useReportsUsage';
import { useReportsPeriod } from '../composables/useReportsPeriod';
import {
  REPORTS_SHELL_PERIOD,
  nearestFixedPeriod,
} from '../helpers/reportsPeriod';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import {
  visibleReportsAreas,
  resolveReportsLocation,
  findTab,
  defaultReportsRouteName,
} from '../helpers/reportsHome';

// A casa dos relatorios. Uma pagina, abas por area, sub-abas dentro da area.
//
// Esta e uma CASCA: nenhuma tela de relatorio foi reescrita. Cada sub-aba e uma
// rota que continua montando o mesmo componente de sempre, entao todo link
// antigo continua abrindo, agora com a casa em volta.
//
// O periodo e UM so, escolhido aqui na casca (ReportsPeriodBar) e guardado na
// URL (`?from=&to=&range=`). As abas navegam levando a query, e cada tela le o
// periodo por inject(REPORTS_SHELL_PERIOD) em vez de ter seletor proprio.

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const { shouldShow } = usePolicy();
const { accountId } = useAccount();

const { usage } = useReportsUsage(accountId);

const canShow = ({ featureFlag, permissions }) =>
  shouldShow(featureFlag, permissions);

const context = computed(() => ({ canShow, usage: usage.value }));

// A aba de area leva para a primeira sub-aba que a pessoa pode ver.
const areas = computed(() =>
  visibleReportsAreas(context.value).map(area => ({
    ...area,
    routeName: area.tabs[0].routeName,
  }))
);

const location = computed(() => resolveReportsLocation(route.name));

const activeAreaId = computed(() => location.value?.areaId || '');
const activeTabId = computed(() => location.value?.tabId || '');

const activeArea = computed(
  () => areas.value.find(area => area.id === activeAreaId.value) || null
);

// A aba atual pode nao estar na lista visivel (CSAT aberto por link direto numa
// conta sem uso, por exemplo). A rota abre do mesmo jeito; a casa so nao
// destaca uma aba que nao mostra.
const activeTabDefinition = computed(() =>
  findTab(activeAreaId.value, activeTabId.value)
);

const showsPeriod = computed(
  () => activeTabDefinition.value?.acceptsPeriod !== false
);
// So a tela ao vivo nao tem recorte nenhum: ela diz isso no lugar do seletor.
const showsNoPeriodNotice = computed(() => !showsPeriod.value);

const { period, periodKey, setPeriod } = useReportsPeriod();
provide(REPORTS_SHELL_PERIOD, { period, periodKey });

const onPreset = range => setPeriod({ range });
const onCustom = ({ startDay, endDay }) =>
  setPeriod({ range: 'custom', startDay, endDay });

// "Comparar com o periodo anterior" so aparece na aba cuja tela compara.
const showsCompare = computed(
  () => activeTabDefinition.value?.supportsCompare === true
);
const compare = computed(() => route.query.compare === '1');
const setCompare = value => {
  const query = { ...route.query };
  if (value) query.compare = '1';
  else delete query.compare;
  router.replace({ name: route.name, params: route.params, query });
};

// Aba que so aceita periodos fixos (Painel da IA) mostra o fixo mais proximo e
// diz qual, com as datas escolhidas, quando nao e o mesmo periodo.
const fixedPeriod = computed(() =>
  activeTabDefinition.value?.fixedPeriods
    ? nearestFixedPeriod(period.value)
    : null
);
const shortDay = day =>
  new Intl.DateTimeFormat(toIntlLocale(locale?.value), {
    day: '2-digit',
    month: '2-digit',
  }).format(day);

const subTabs = computed(() => activeArea.value?.tabs || []);

// O painel e nomeado pela aba que o abriu: a sub-aba quando a fileira dela
// aparece, senao a area. Aba ativa fora da lista (link direto para CSAT numa
// conta sem uso) nao tem elemento para apontar, e o painel fica sem nome em
// vez de apontar para um id que nao existe.
const tabpanelLabelledBy = computed(() => {
  if (
    subTabs.value.length > 1 &&
    subTabs.value.some(tab => tab.id === activeTabId.value)
  ) {
    return `reports-tab-${activeTabId.value}`;
  }
  if (activeArea.value) return `reports-area-${activeAreaId.value}`;
  return undefined;
});

// Quem abre /reports sem dizer a aba cai na primeira que pode ver.
watch(
  () => [route.name, areas.value.length],
  () => {
    if (route.name !== 'reports_home') return;
    const target = defaultReportsRouteName(context.value);
    if (target) {
      router.replace({
        name: target,
        params: route.params,
        query: route.query,
      });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="overflow-x-hidden overflow-y-auto bg-n-surface-1 w-full px-4 lg:px-6"
    data-testid="page-reports"
  >
    <div class="max-w-5xl mx-auto pb-12">
      <header class="flex flex-col gap-4 pt-6">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <h1 class="text-heading-1 text-n-slate-12 m-0">
            {{ t('REPORT.HOME.TITLE') }}
          </h1>
          <div class="flex flex-col items-start lg:items-end gap-1">
            <ReportTimezoneNote />
            <p
              v-if="showsNoPeriodNotice"
              class="text-xs leading-5 text-n-slate-11 m-0"
              data-testid="reports-no-period"
            >
              {{ t('REPORT.HOME.NO_PERIOD') }}
            </p>
          </div>
        </div>

        <ReportsHomeTabs
          v-if="areas.length"
          :items="areas"
          :active-id="activeAreaId"
          :label="t('REPORT.HOME.AREAS_LABEL')"
          :query="route.query"
          id-prefix="reports-area"
          show-icon
          data-testid="reports-area-tabs"
        />

        <ReportsPeriodBar
          v-if="showsPeriod"
          :period="period"
          :show-compare="showsCompare"
          :compare="compare"
          @preset="onPreset"
          @custom="onCustom"
          @update:compare="setCompare"
        />

        <ReportsHomeTabs
          v-if="subTabs.length > 1"
          :items="subTabs"
          :active-id="activeTabId"
          :label="t('REPORT.HOME.SUBTABS_LABEL')"
          :query="route.query"
          data-testid="reports-sub-tabs"
        />
      </header>

      <div
        id="reports-tabpanel"
        role="tabpanel"
        :aria-labelledby="tabpanelLabelledBy"
        class="min-w-0"
      >
        <p
          v-if="fixedPeriod && !fixedPeriod.exact"
          role="status"
          class="mt-5 mb-0 flex items-start gap-2.5 rounded-xl bg-n-amber-2 px-3.5 py-3 text-sm leading-relaxed text-n-amber-12"
          data-testid="reports-fixed-period-notice"
        >
          <span
            class="i-ph-info size-[18px] shrink-0 mt-0.5 text-n-amber-11"
            aria-hidden="true"
          />
          <span>
            {{ t('REPORT.PERIOD_BAR.FIXED_NOTICE.LEAD') }}
            <strong>{{ t(fixedPeriod.labelKey) }}</strong
            >{{
              t('REPORT.PERIOD_BAR.FIXED_NOTICE.TAIL', {
                start: shortDay(period.startDay),
                end: shortDay(period.endDay),
              })
            }}
          </span>
        </p>
        <router-view />
      </div>
    </div>
  </div>
</template>
